import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { getById, post } from '../_request';
import { useToast } from '../../modules/layout/components/ToastContext';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import { Trash } from 'react-bootstrap-icons';
import { IAxiosGetResponse } from '../../interfaces/IUtilities';

interface DependencyUploadInput {
    file: File;
    isTemplate: boolean;
}

interface LibraryFormValues {
    libraryPath: string;
    libraryFile: File | null;
    dependencies: DependencyUploadInput[];
}

const EditLibraryForm: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { libraryId, serviceid, transactionid } = useParams<{
        libraryId: string;
        serviceid: string;
        transactionid: string;
    }>();
    const { showToast } = useToast();

    const [libraryPath, setLibraryPath] = useState('');
    const [service, setService] = useState('');
    const [transaction, setTransaction] = useState('');

    useEffect(() => {
        const fetchServiceName = async () => {
            try {
                let result = await getById("/api/services/", serviceid! + "/name") as IAxiosGetResponse;
                if (result?.response.status === 200) setService(result.data as string);
            } catch { }
        };

        const fetchClassName = async () => {
            try {
                let result = await getById("/api/transactionroutes/", transactionid! + "/classname") as IAxiosGetResponse;
                if (result?.response.status === 200) setTransaction(result.data as string);
            } catch { }
        };

        const fetchLibraryPath = async () => {
            try {
                if (!libraryId) return;
                let result = await getById('/api/library/getlibrarypath/', libraryId);
                let respStatus = result?.status || result?.response.status;
        
                if (respStatus === 200) setLibraryPath(result.data as string);
                else if (respStatus === 403) navigate("/error/403");
            } catch { }
        };

        fetchServiceName();
        fetchClassName();
        fetchLibraryPath();
    }, [libraryId, serviceid, transactionid]);

    const validationSchema = Yup.object({
        libraryFile: Yup.mixed<File>()
            .required(t('MainLibraryFileRequired'))
            .test('fileExists', t('MainLibraryFileRequired'), (value) => value instanceof File),
    });

    const initialValues: LibraryFormValues = {
        libraryPath: libraryPath,
        libraryFile: null,
        dependencies: [],
    };

    const handleSubmit = async (values: LibraryFormValues) => {
        if (!libraryId || !transactionid) {
            showToast(t('TransactionRouteIdMissing'), 'error');
            return;
        }

        const formData = new FormData();
        if (values.libraryFile) {
            formData.append('library', values.libraryFile);
        }
        formData.append('libraryPath', libraryPath);
        formData.append('libraryId', libraryId);

        values.dependencies.forEach((dep, index) => {
            formData.append(`dependencies[${index}].Dependency`, dep.file);
            formData.append(`dependencies[${index}].IsTemplate`, String(dep.isTemplate));
        });

        try {
            const result = await post('/api/library/editlibrary', formData, true);
            if (result.response.status === 200) {
                showToast(t('LibraryUpdated', 'Library updated successfully!'), 'success');
                navigate(`/services/${serviceid}/transactions/${transactionid}/libraries/list`);
            } else {
                showToast(t('UploadError', 'Failed to update library'), 'error');
            }
        } catch (error: any) {
            showToast(t('ConnectionError', { error: error.message || error.toString() }), 'error');
        }
    };

    return (
        <div className="container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t('Home'), href: '/' },
                    { title: t('Services'), href: '/services/list' },
                    { title: `${service}: ${t("TransactionRoutes")}`, href: `/services/${serviceid}/transactions/list` },
                    { title: `${transaction}: ${t("Libraries")}`, href: `/services/${serviceid}/transactions/${transactionid}/libraries/list` },
                    { title: t('EditLibrary') },
                ]}
            />

            <h3>{t('EditLibrary')}</h3>

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        {/* Library Path */}
                        <div className="mb-3">
                            <label className="form-label">{t('LibraryPath')}</label>
                            <Field name="libraryPath" className="form-control" value={libraryPath} disabled />
                        </div>

                        {/* Main Library File */}
                        <div className="mb-3">
                            <label className="form-label">
                                {t('MainLibraryFile')} <span className="text-danger">*</span>
                            </label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={(e) => {
                                    if (e.currentTarget.files?.[0]) {
                                        setFieldValue('libraryFile', e.currentTarget.files[0]);
                                    }
                                }}
                            />
                            {touched.libraryFile && errors.libraryFile && (
                                <div className="text-danger">{errors.libraryFile}</div>
                            )}
                        </div>

                        {/* Dependencies */}
                        <div className="form-group mt-3">
                            <label className="form-label">{t('Dependencies')}</label>
                            <input
                                type="file"
                                className="form-control"
                                multiple
                                onChange={(e) => {
                                    const files = Array.from(e.currentTarget.files || []);
                                    setFieldValue('dependencies', [
                                        ...values.dependencies,
                                        ...files.map((f) => ({
                                            file: f,
                                            isTemplate: false,
                                        })),
                                    ]);
                                    e.currentTarget.value = '';
                                }}
                            />

                            {values.dependencies.length > 0 && (
                                <div className="mt-3">
                                    <label className="form-label">{t('SelectedFiles')}</label>
                                    <ul className="list-group">
                                        {values.dependencies.map((dep, index) => (
                                            <li
                                                key={index}
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                            >
                                                <span className="text-truncate" style={{ maxWidth: '60%' }}>
                                                    {dep.file.name}
                                                </span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="form-check me-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={dep.isTemplate}
                                                            onChange={(e) => {
                                                                const newDeps = [...values.dependencies];
                                                                newDeps[index].isTemplate = e.target.checked;
                                                                setFieldValue('dependencies', newDeps);
                                                            }}
                                                        />
                                                        <label className="form-check-label">{t('Template')}</label>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => {
                                                            const newDeps = values.dependencies.filter((_, i) => i !== index);
                                                            setFieldValue('dependencies', newDeps);
                                                        }}
                                                    >
                                                        <Trash size={16} />
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <button type="submit" className="btn gg-btn-submit mt-4">
                            {t('UpdateLibrary')}
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EditLibraryForm;
